import React from 'react';
import {WeatherService} from '../api';
import {WeatherWarningListResponse} from '../api/types';
import {Dates} from '../utils';
import useApiClient from './useApiClient';

/** Stored warnings older than this are refetched. */
const STALE_AFTER_MINUTES = 60;

/**
 * How often `autoRefresh` re-checks staleness. A check is a timestamp compare;
 * it fetches only once the data is past `STALE_AFTER_MINUTES`.
 */
export const WARNINGS_REFRESH_CHECK_MS = 60000;

export type WeatherWarningsOptions = {
  /**
   * Fetch on mount when the stored warnings are stale, then re-check every
   * minute while mounted. Off by default: the app bars still poll themselves
   * until they adopt this.
   */
  autoRefresh?: boolean;
};

/**
 * Stale when there is nothing stored, or the stored response has no usable
 * `computed_at`. The backend answers 200 with an EMPTY body when serialization
 * fails, which stores a response without `computed_at`: `moment.unix(undefined)`
 * then diffs to NaN minutes, `NaN > 60` is false, and that response used to
 * count as fresh forever.
 */
export const isWarningResponseStale = (
  data: WeatherWarningListResponse | null | undefined,
): boolean => {
  const computedAt = data?.computed_at;
  if (typeof computedAt !== 'number' || !Number.isFinite(computedAt)) {
    return true;
  }
  return !(Dates.timeDiffUtcWithNowInMin(computedAt) <= STALE_AFTER_MINUTES);
};

/**
 * The fetch half shared by `useStationWarning` and `useLocationWarning`.
 *
 * The in-flight guard is a ref, not state: two calls in the same tick (an app
 * bar's mount effect and its first interval tick, or two screens mounting
 * together) both read the same `isUpdating` closure and both fetched. A ref
 * also keeps `update` referentially stable across a fetch, so effects that
 * depend on it are not torn down and re-armed by every request.
 */
const useWeatherWarningsSync = ({
  storeKey,
  lat,
  lon,
  data,
  onData,
  autoRefresh,
}: {
  /** The store slot (station id or location hash); falsy disables fetching. */
  storeKey: string | number | null | undefined;
  lat: number | null | undefined;
  lon: number | null | undefined;
  data: WeatherWarningListResponse | null | undefined;
  /** Stores a response; must close over `storeKey` (stable per place). */
  onData: (response: WeatherWarningListResponse) => void;
  autoRefresh: boolean;
}) => {
  const {fetch} = useApiClient();
  // The place a request was made for. The guard and the delivery are both
  // keyed by it: a fetch still in flight when the station or location changes
  // neither blocks the new place's first fetch nor lands in its slot.
  const requestKey =
    storeKey && lat != null && lon != null && Number.isFinite(lat + lon)
      ? `${storeKey}|${lat}|${lon}`
      : null;
  const currentKey = React.useRef(requestKey);
  // Layout effect: committed before any passive effect (or a response that
  // resolves after the commit) can read it.
  React.useLayoutEffect(() => {
    currentKey.current = requestKey;
  }, [requestKey]);
  const inFlight = React.useRef<string | null>(null);
  const [isUpdating, setIsUpdating] = React.useState(false);
  // The place whose last fetch failed, if any. The fetch stays silent (no
  // snackbar); the list screen reads `hasError` to show an error state rather
  // than let an outage pass for "no warnings". Keyed, so a failure at one
  // place never shows at the next.
  const [failedKey, setFailedKey] = React.useState<string | null>(null);
  const hasError = failedKey !== null && failedKey === requestKey;

  const update = React.useCallback(async () => {
    if (requestKey === null || inFlight.current === requestKey) {
      return;
    }
    inFlight.current = requestKey;
    setIsUpdating(true);
    try {
      const response: WeatherWarningListResponse = await fetch(
        WeatherService.locationWeatherWarnings({
          lat: lat as number,
          lon: lon as number,
        }),
        // A missing warning is not the user's problem, and this runs on a
        // timer: never raise the global error snackbar from it.
        {ignore404: true, silent: true},
      );
      if (currentKey.current === requestKey) {
        onData(response);
        // A 200 without a usable `computed_at` (the backend's empty body when
        // serialization fails) is a failure too: nothing usable arrived.
        const usable =
          typeof response?.computed_at === 'number' &&
          Number.isFinite(response.computed_at);
        setFailedKey(usable ? null : requestKey);
      }
    } catch {
      // Silent by design: the card keeps what it had and the next check retries.
      if (currentKey.current === requestKey) {
        setFailedKey(requestKey);
      }
    } finally {
      if (inFlight.current === requestKey) {
        inFlight.current = null;
        setIsUpdating(false);
      }
    }
  }, [requestKey, lat, lon, fetch, onData]);

  const isUpdateRequired = React.useCallback(
    () => isWarningResponseStale(data),
    [data],
  );

  // Read through refs so the interval is armed once per place, not re-armed on
  // every store update.
  const isUpdateRequiredRef = React.useRef(isUpdateRequired);
  React.useEffect(() => {
    isUpdateRequiredRef.current = isUpdateRequired;
  }, [isUpdateRequired]);

  React.useEffect(() => {
    if (!autoRefresh) {
      return undefined;
    }
    const check = () => {
      if (isUpdateRequiredRef.current()) {
        update();
      }
    };
    check();
    const interval = setInterval(check, WARNINGS_REFRESH_CHECK_MS);
    return () => clearInterval(interval);
  }, [autoRefresh, update]);

  const computedAt =
    typeof data?.computed_at === 'number' && Number.isFinite(data.computed_at)
      ? data.computed_at
      : null;

  return {update, isUpdateRequired, isUpdating, hasError, computedAt};
};

export default useWeatherWarningsSync;
